// URL de l'API - toujours relative pour cohérence
const API_URL = import.meta.env.VITE_API_URL || '/cv/api';

/**
 * Récupère le PDF depuis l'API avec authentification JWT
 * @param {string} token - Token JWT
 * @returns {Promise<string>} URL du blob PDF
 */
export async function fetchPDF(token) {
  try {
    // Construire l'URL correctement (éviter double /api)
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/pdf/view` 
      : `${API_URL}/api/pdf/view`;
    
    console.log('🔍 Fetching PDF from:', url);
    console.log('🔑 Token:', token.substring(0, 20) + '...');
    
    // Vérifier d'abord si le token est valide en faisant une requête HEAD
    const checkResponse = await fetch(url, {
      method: 'HEAD',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!checkResponse.ok) {
      // Récupérer le message d'erreur
      const errorResponse = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!errorResponse.ok) {
        const errorData = await errorResponse.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Erreur ${errorResponse.status}`);
      }
    }
    
    // Option simple : retourner directement l'URL avec le token
    // react-pdf fera la requête lui-même
    return {
      url: url,
      httpHeaders: {
        'Authorization': `Bearer ${token}`,
      },
    };
  } catch (error) {
    console.error('❌ Erreur lors de la récupération du PDF:', error);
    throw error;
  }
}

/**
 * Génère un token JWT pour tester (dev uniquement)
 * @param {Object} data - Données du recruteur
 * @returns {Promise<Object>} Réponse avec token
 */
export async function generateToken(data) {
  try {
    // Récupérer le token admin depuis sessionStorage
    const adminToken = sessionStorage.getItem('adminToken');
    
    if (!adminToken) {
      throw new Error('Token admin non trouvé. Veuillez vous reconnecter.');
    }
    
    // Construire l'URL correctement (éviter double /api)
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/auth/generate-access` 
      : `${API_URL}/api/auth/generate-access`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-token': adminToken,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Erreur ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la génération du token:', error);
    throw error;
  }
}

/**
 * Enregistre un téléchargement de PDF
 * @param {string} token - Token JWT
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function trackDownload(token) {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/pdf/track-download` 
      : `${API_URL}/api/pdf/track-download`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error('Erreur tracking download:', response.status);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors du tracking du téléchargement:', error);
    // Ne pas bloquer le téléchargement si le tracking échoue
  }
}

/**
 * Récupère tous les tokens depuis la base de données
 * @returns {Promise<Array>} Liste des tokens
 */
export async function fetchAllTokens() {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/admin/tokens` 
      : `${API_URL}/api/admin/tokens`;
    
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }

    const data = await response.json();
    return data.tokens || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des tokens:', error);
    throw error;
  }
}

/**
 * Révoque un token (le rend inutilisable)
 * @param {number} tokenId - ID du token à révoquer
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function revokeToken(tokenId) {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/admin/tokens/${tokenId}/revoke` 
      : `${API_URL}/api/admin/tokens/${tokenId}/revoke`;
    
    const response = await fetch(url, {
      method: 'POST',
      // Pas de Content-Type car pas de body
    });

    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la révocation du token:', error);
    throw error;
  }
}

/**
 * Supprime définitivement un token
 * @param {number} tokenId - ID du token à supprimer
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function deleteToken(tokenId) {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/admin/tokens/${tokenId}` 
      : `${API_URL}/api/admin/tokens/${tokenId}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la suppression du token:', error);
    throw error;
  }
}

/**
 * Met à jour les propriétés d'un token
 * @param {number} tokenId - ID du token à modifier
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function updateToken(tokenId, data) {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/admin/tokens/${tokenId}` 
      : `${API_URL}/api/admin/tokens/${tokenId}`;
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la mise à jour du token:', error);
    throw error;
  }
}

/**
 * Prolonge un token de X heures
 * @param {number} tokenId - ID du token à prolonger
 * @param {number} hours - Nombre d'heures à ajouter
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function extendToken(tokenId, hours = 48) {
  try {
    const url = API_URL.endsWith('/api') 
      ? `${API_URL}/admin/tokens/${tokenId}/extend` 
      : `${API_URL}/api/admin/tokens/${tokenId}/extend`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ hours }),
    });

    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la prolongation du token:', error);
    throw error;
  }
}
